import { memo, useCallback, useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Icon } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { MathJaxHtml } from "../../../../../components/mathJax/MathJaxHtml";
import type { QuestionData } from "../models/GetQuestions";
import { QUESTION_TYPES } from "../types/questionTypes";
import { ExerciseReportModalWeb } from "./ExerciseReportModalWeb";
import { MatrixTwoColumnScreen } from "./matrix-two-column-type/MatrixTwoColumnScreen";
import { MultipleQuestionTypeScreen } from "./multiple-question-type/MultipleQuestionTypeScreen";
import { NumericQuestionTypeScreen } from "./numeric-question-type/NumericQuestionTypeScreen";
import {
  ReviseSolutionWeb,
  type ReviseSolutionSessionStats,
  type ReviseSolutionVideoPlayerPayload,
} from "./ReviseSolutionWeb";
import { SingleQuestionTypeScreen } from "./single-question-type/SingleQuestionTypeScreen";
import { SubjectiveQuestionTypeScreen } from "./subjective-question-type/SubjectiveQuestionTypeScreen";
import styles from "./ReviseQuestionContainer.module.scss";

export type ReviseQuestionContainerProps = {
  questionData: QuestionData;
  currentIndex: number;
  totalQuestions: number;
  onNext?: () => void;
  onPrev?: () => void;
  onSubmit?: (userAnswer: string, timeTakenInSecs: number) => void;
  onSubmitLikeDislike?: (isLiked: boolean, isDisliked: boolean) => void;
  onFinish?: () => void;
  isNarrowLayout: boolean;
  isActive: boolean;
  examId: number;
  gradeId: number;
  onOpenVideo: (payload: ReviseSolutionVideoPlayerPayload) => void;
  solutionSessionStats?: ReviseSolutionSessionStats | null;
  onDraftAnswerChange?: (questionId: string, draft: string) => void;
};

export const ReviseQuestionContainer = ({
  questionData,
  currentIndex,
  totalQuestions,
  onNext: onNextProp,
  onPrev: onPrevProp,
  onSubmit: onSubmitProp,
  onSubmitLikeDislike,
  onFinish,
  isNarrowLayout,
  isActive,
  examId,
  gradeId,
  onOpenVideo,
  solutionSessionStats,
  onDraftAnswerChange,
}: ReviseQuestionContainerProps) => {
  const { theme } = useTheme();
  const { t } = useTranslation("selflearn");
  const reduceMotion = useReducedMotion();

  const [tempSelectedValue, setTempSelectedValue] = useState("");
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [isSolutionView, setIsSolutionView] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportAssetName, setReportAssetName] = useState("");
  const [isReportFeedback, setIsReportFeedback] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const accumulatedTimeRef = useRef(0);

  const videoId = Number(questionData.question?.video_solution_id) || 0;

  useEffect(() => {
    setTempSelectedValue(
      questionData.isAttempted && questionData.userAnswer
        ? questionData.userAnswer
        : (questionData.userAnswer ?? ""),
    );
    setIsSolutionView(false);
    accumulatedTimeRef.current = 0;
    setTimerSeconds(0);
  }, [
    questionData.question_id,
    questionData.isAttempted,
    questionData.userAnswer,
  ]);

  const handleSelectionChange = useCallback(
    (next: string) => {
      setTempSelectedValue(next);
      if (!questionData.isAttempted) {
        onDraftAnswerChange?.(String(questionData.question_id ?? ""), next);
      }
    },
    [onDraftAnswerChange, questionData.isAttempted, questionData.question_id],
  );

  useEffect(() => {
    if (questionData.isAttempted || !isActive) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    setTimerSeconds(accumulatedTimeRef.current);
    timerRef.current = setInterval(() => {
      accumulatedTimeRef.current += 1;
      setTimerSeconds(accumulatedTimeRef.current);
    }, 1000);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [questionData.question_id, questionData.isAttempted, isActive]);

  const displaySeconds = questionData.isAttempted
    ? (questionData.timeTakenInSecs ?? 0)
    : timerSeconds;

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}s`;
  };

  const onFlag = useCallback(() => {
    setReportAssetName("questionReport");
    setIsReportFeedback(false);
    setIsReportModalOpen(true);
  }, []);

  const handleLikePress = useCallback(() => {
    if (questionData.isLiked) {
      onSubmitLikeDislike?.(false, false);
    } else {
      onSubmitLikeDislike?.(true, false);
    }
  }, [onSubmitLikeDislike, questionData.isLiked]);

  const handleDislikePress = useCallback(() => {
    if (questionData.isDisliked) {
      onSubmitLikeDislike?.(false, false);
    } else {
      onSubmitLikeDislike?.(false, true);
    }
  }, [onSubmitLikeDislike, questionData.isDisliked]);

  const handleCloseReportModal = useCallback(() => {
    setIsReportModalOpen(false);
  }, []);

  const onNext = useCallback(() => {
    if (currentIndex < totalQuestions - 1) {
      onNextProp?.();
    }
  }, [currentIndex, totalQuestions, onNextProp]);

  const onPrevious = useCallback(() => {
    if (currentIndex > 0) {
      if (!questionData.isAttempted) {
        setTempSelectedValue("");
        onDraftAnswerChange?.(String(questionData.question_id ?? ""), "");
      }
      onPrevProp?.();
    }
  }, [
    onDraftAnswerChange,
    onPrevProp,
    questionData.isAttempted,
    questionData.question_id,
    currentIndex,
  ]);

  const onSubmit = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onSubmitProp?.(tempSelectedValue, timerSeconds);
  }, [onSubmitProp, tempSelectedValue, timerSeconds]);

  const isAttempted = questionData.isAttempted;
  const hasSelection = !!tempSelectedValue;
  const isPreviousDisabled = currentIndex === 0;
  const isLastQuestion = currentIndex >= totalQuestions - 1;
  const isFinish = isLastQuestion && !!isAttempted;

  const handleRightPress = () => {
    if (isFinish) {
      onFinish?.();
    } else if (isLastQuestion && !isAttempted && !hasSelection) {
      onFinish?.();
    } else if (!isAttempted && !hasSelection) {
      onNext();
    } else if (!isAttempted && hasSelection) {
      onSubmit();
    } else {
      onNext();
    }
  };

  const footerPreviousSlotClass = isNarrowLayout
    ? styles.footerGrow
    : styles.footerPreviousSlot;
  const footerNextSlotClass = isNarrowLayout
    ? styles.footerGrow
    : styles.footerNextSlot;

  const showSolutionCta = isAttempted && !isSolutionView;
  const solutionButton = (
    <button
      type="button"
      className={styles.solutionPill}
      onClick={() => setIsSolutionView(true)}
    >
      <Icon name="bulbIcon" size={20} />
      <span>{t("solution")}</span>
    </button>
  );

  const blockQuestionPointer =
    isSolutionView && isNarrowLayout ? "none" : "auto";
  const scrollLockedForSolution = isSolutionView && isNarrowLayout;

  const solutionPanelTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.32, ease: [0.32, 0.72, 0, 1] as const };

  const isWideWeb = !isNarrowLayout;

  const solutionSharedProps = {
    t,
    questionData,
    onBack: () => setIsSolutionView(false),
    onLikePress: handleLikePress,
    onDislikePress: handleDislikePress,
    onOpenReportModal: (assetName: string, feedBack: boolean) => {
      setReportAssetName(assetName);
      setIsReportFeedback(feedBack);
      setIsReportModalOpen(true);
    },
    videoId,
    onOpenVideo,
    solutionSessionStats,
  };

  return (
    <div
      className={`${styles.shell} ${!isNarrowLayout ? styles.shellWide : ""}`}
    >
      <div
        className={`${styles.card} ${isWideWeb ? styles.cardWithSolutionWeb : ""}`}
      >
        <div
          className={`${styles.questionMain} ${isWideWeb ? styles.questionMainWithSolutionWeb : ""}`}
        >
          <div
            className={styles.headerRow}
            style={{ pointerEvents: blockQuestionPointer }}
          >
            <div className={styles.timerRow}>
              <Icon name="clockIcon" size={24} />
              <span className={styles.timerText}>
                {formatTime(displaySeconds)}
              </span>
            </div>
            <button
              type="button"
              className={styles.flagBtn}
              onClick={onFlag}
              aria-label={t("report", { defaultValue: "Report" })}
            >
              <Icon name="flag" size={24} color={theme.colors.primary[40]} />
            </button>
          </div>

          <div className={styles.contentArea}>
            <div className={styles.contentBody}>
              <div
                className={`${styles.scrollArea} ${scrollLockedForSolution ? styles.scrollDisabled : ""}`}
              >
                {questionData?.passage_text ? (
                  <div className={styles.passageBlock}>
                    <div className={styles.sectionLabel}>Passage</div>
                    <MathJaxHtml
                      questionNo={questionData.passageNo}
                      className={styles.reviseTextMathStyle}
                      content={questionData.passage_text ?? ""}
                    />
                  </div>
                ) : null}
                <MathJaxHtml
                  questionNo={questionData?.questionNo}
                  className={styles.reviseTextMathStyle}
                  content={questionData?.question?.question_text ?? ""}
                />
                <div className={styles.spacer24} />
                {questionData.question_type ===
                QUESTION_TYPES.ASSERTION_REASON ? (
                  <div>
                    {questionData.question?.assertion ? (
                      <div className={styles.passageBlock}>
                        <div className={styles.sectionLabel}>Assertion</div>
                        <MathJaxHtml
                          className={styles.reviseTextMathStyle}
                          content={questionData.question.assertion ?? ""}
                        />
                      </div>
                    ) : null}
                    {questionData.question?.reason ? (
                      <div className={styles.passageBlock}>
                        <div className={styles.sectionLabel}>Reason</div>
                        <MathJaxHtml
                          className={styles.reviseTextMathStyle}
                          content={questionData.question.reason ?? ""}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {questionData.question_type === QUESTION_TYPES.MMCQ ? (
                  <MultipleQuestionTypeScreen
                    questionData={questionData}
                    selectedValue={tempSelectedValue}
                    setSelectedValue={handleSelectionChange}
                  />
                ) : null}

                {questionData.question_type === QUESTION_TYPES.NUMERIC ||
                questionData.question_type === QUESTION_TYPES.INTEGER ? (
                  <NumericQuestionTypeScreen
                    questionData={questionData}
                    selectedValue={tempSelectedValue}
                    setSelectedValue={handleSelectionChange}
                  />
                ) : null}

                {[
                  QUESTION_TYPES.SUBJECTIVE_VERY_SHORT,
                  QUESTION_TYPES.SUBJECTIVE_SHORT,
                  QUESTION_TYPES.SUBJECTIVE_LONG,
                ].some((q) => q === questionData.question_type) ? (
                  <SubjectiveQuestionTypeScreen
                    questionData={questionData}
                    selectedValue={tempSelectedValue}
                    setSelectedValue={handleSelectionChange}
                    onDone={onSubmit}
                  />
                ) : null}

                {questionData.question_type ===
                QUESTION_TYPES.MATRIX_TWO_COLUMN ? (
                  <MatrixTwoColumnScreen
                    questionData={questionData}
                    selectedValue={tempSelectedValue}
                    setSelectedValue={handleSelectionChange}
                    onDone={onSubmit}
                  />
                ) : null}

                {[
                  QUESTION_TYPES.SCMCQ,
                  QUESTION_TYPES.TRUE_FALSE,
                  QUESTION_TYPES.MATRIX_SCMCQ,
                  QUESTION_TYPES.ASSERTION_REASON,
                ].some((q) => q === questionData.question_type) ? (
                  <SingleQuestionTypeScreen
                    questionData={questionData}
                    selectedValue={tempSelectedValue}
                    setSelectedValue={handleSelectionChange}
                  />
                ) : null}
              </div>
            </div>

            {showSolutionCta && isNarrowLayout ? (
              <div className={styles.solutionPillWrap}>{solutionButton}</div>
            ) : null}
          </div>

          <div
            className={`${styles.footer} ${!isNarrowLayout ? styles.footerWide : ""}`}
            style={{ pointerEvents: blockQuestionPointer }}
          >
            {showSolutionCta && !isNarrowLayout ? (
              <div className={styles.footerSolutionSlot}>{solutionButton}</div>
            ) : null}
            <div className={footerPreviousSlotClass}>
              <button
                type="button"
                className={`${styles.pill} ${isPreviousDisabled ? styles.pillDisabled : styles.pillGhost}`}
                style={
                  !isPreviousDisabled && !isNarrowLayout
                    ? {
                        color: theme.colors.primary[40],
                        boxShadow: "none",
                      }
                    : undefined
                }
                disabled={isPreviousDisabled}
                onClick={onPrevious}
              >
                {t("previous", { defaultValue: "Previous" })}
              </button>
            </div>
            <div className={footerNextSlotClass}>
              <button
                type="button"
                className={`${styles.pill} ${styles.pillPrimary}`}
                onClick={handleRightPress}
              >
                {isFinish
                  ? t("finish")
                  : !isAttempted && !hasSelection
                    ? t("skip")
                    : !isAttempted && hasSelection
                      ? t("submit")
                      : t("next")}
              </button>
            </div>
          </div>
        </div>

        {isNarrowLayout && isSolutionView ? (
          <div className={styles.solutionOverlay}>
            <ReviseSolutionWeb {...solutionSharedProps} variant="overlay" />
          </div>
        ) : null}

        {isWideWeb ? (
          <motion.div
            className={styles.solutionPanelWebSlot}
            initial={false}
            animate={{ width: isSolutionView ? "37%" : "0%" }}
            transition={solutionPanelTransition}
          >
            {isSolutionView ? (
              <div className={styles.solutionPanelWeb}>
                <ReviseSolutionWeb {...solutionSharedProps} variant="drawer" />
              </div>
            ) : null}
          </motion.div>
        ) : null}

        <ExerciseReportModalWeb
          isOpen={isReportModalOpen}
          onClose={handleCloseReportModal}
          assetName={reportAssetName}
          examId={examId}
          gradeId={gradeId}
          feedBack={isReportFeedback}
        />
      </div>
    </div>
  );
};

const MemoReviseQuestionContainer = memo(
  ReviseQuestionContainer,
  (prev, next) =>
    prev.questionData.question_id === next.questionData.question_id &&
    prev.questionData.isAttempted === next.questionData.isAttempted &&
    prev.questionData.userAnswer === next.questionData.userAnswer &&
    prev.questionData.isLiked === next.questionData.isLiked &&
    prev.questionData.isDisliked === next.questionData.isDisliked &&
    prev.isActive === next.isActive &&
    prev.currentIndex === next.currentIndex &&
    prev.totalQuestions === next.totalQuestions &&
    prev.isNarrowLayout === next.isNarrowLayout &&
    prev.examId === next.examId &&
    prev.gradeId === next.gradeId &&
    prev.onOpenVideo === next.onOpenVideo &&
    prev.onDraftAnswerChange === next.onDraftAnswerChange &&
    prev.solutionSessionStats?.accuracyPercent ===
      next.solutionSessionStats?.accuracyPercent &&
    prev.solutionSessionStats?.attemptCount ===
      next.solutionSessionStats?.attemptCount,
);

export { MemoReviseQuestionContainer as MemoizedReviseQuestionContainer };
